import { expireAdminSession } from './adminSession';

/**
 * API Client for PlateOS
 * 
 * Provides a centralized way to make authenticated API calls.
 * Automatically attaches the JWT token from localStorage to all requests.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get the current auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('plateos_token');
};

/**
 * RBAC v4.17 — 403 means "authenticated but not allowed". Announce it (a
 * single "no permission" toast in the admin shell) but never logout/redirect
 * (that is 401 behavior). Shared by every request path so JSON and FormData
 * stay in sync.
 */
const announceForbidden = (endpoint) => {
  try {
    window.dispatchEvent(new CustomEvent('plateos:forbidden', { detail: { url: endpoint } }));
  } catch { /* non-browser env — ignore */ }
};

/**
 * Make an authenticated API request
 * 
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Keep Authorization header as fallback for existing sessions
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Use the pristine fetch reference captured in index.html *before* any
  // platform/telemetry script monkey-patched ``window.fetch`` and consumed
  // error-response bodies for observability. Falls back to ``fetch`` if the
  // snapshot is missing for any reason (e.g. SSR / tests).
  const _fetch = (typeof window !== 'undefined' && window.__pristineFetch) || fetch;
  const response = await _fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',  // Send httpOnly cookies
  });
  
  if (response.status === 403) {
    announceForbidden(endpoint);
  }

  if (response.status === 401) expireAdminSession(token);

  return response;
};

/**
 * GET request
 */
export const apiGet = async (endpoint, options = {}) => {
  return apiRequest(endpoint, { ...options, method: 'GET' });
};

/**
 * POST request
 */
export const apiPost = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request
 */
export const apiPut = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * PATCH request
 */
export const apiPatch = async (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request
 */
export const apiDelete = async (endpoint, data = null) => {
  const options = { method: 'DELETE' };
  if (data) {
    options.body = JSON.stringify(data);
  }
  return apiRequest(endpoint, options);
};

/**
 * POST with FormData (for file uploads)
 */
export const apiPostFormData = async (endpoint, formData) => {
  const token = getAuthToken();
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData, // Don't set Content-Type for FormData
    credentials: 'include',  // Send httpOnly cookies
  });

  if (response.status === 403) {
    announceForbidden(endpoint);
  }

  if (response.status === 401) expireAdminSession(token);

  return response;
};

export default {
  apiRequest,
  apiGet,
  apiPost,
  apiPut,
  apiPatch,
  apiDelete,
  apiPostFormData,
};
